import { z } from "zod/v4"
import { DocumentSnapshotLike } from "../firestore"
import { PowerId, PowerIdSchema } from "../id"

export const PowerStatus = z.enum(["public", "hidden"])

export const PowerDenormalizedCommon = z.object({
  name: z.string(),
  icon_setting: z.object({
    url: z.string(),
  }),
  effect_setting: z.object({
    url: z.string(),
  }),
  price: z.number(),
  impact_value: z.number(),
})

export const PowerDenormalized = z.intersection(
  PowerDenormalizedCommon,
  z.object({
    id: PowerIdSchema,
  })
)

export type PowerDenormalized = z.infer<typeof PowerDenormalized>

export const PowerDocumentMeta = z.intersection(
  PowerDenormalizedCommon,
  z.object({
    sales_start_at: z.number().optional(),
    sales_end_at: z.number().optional(),
    status: PowerStatus,
  })
)

export type PowerDocumentMeta = z.infer<typeof PowerDocumentMeta>

export const PowerDocumentBody = z.intersection(
  PowerDocumentMeta,
  z.object({
    created_at: z.number(),
    updated_at: z.number(),
  })
)

export type PowerDocumentBody = z.infer<typeof PowerDocumentBody>

export const PowerDocument = z.intersection(
  PowerDocumentBody,
  z.object({
    id: PowerIdSchema,
  })
)

export type PowerDocument = z.infer<typeof PowerDocument>

export class Powers {

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): PowerDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }
    return {
      id: PowerId(doc.id),
      name: data.name,
      icon_setting: data.icon_setting,
      effect_setting: data.effect_setting,
      price: data.price ?? 0,
      impact_value: data.impact_value ?? 0,
      sales_start_at: data.sales_start_at,
      sales_end_at: data.sales_end_at,
      status: data.status,
      created_at: data.created_at,
      updated_at: data.updated_at,
    }
  }

  static toDocumentData(data: PowerDocumentBody): PowerDocumentBody {
    const result: PowerDocumentBody = {
      name: data.name,
      icon_setting: data.icon_setting,
      effect_setting: data.effect_setting,
      price: data.price,
      impact_value: data.impact_value,
      status: data.status,
      created_at: data.created_at,
      updated_at: data.updated_at,
    }

    if (data.sales_start_at !== undefined) {
      result.sales_start_at = data.sales_start_at
    }
    if (data.sales_end_at !== undefined) {
      result.sales_end_at = data.sales_end_at
    }

    return result
  }

  public static toDenormalized(power: PowerDocument): PowerDenormalized {
    return {
      id: power.id,
      name: power.name,
      icon_setting: power.icon_setting,
      effect_setting: power.effect_setting,
      price: power.price,
      impact_value: power.impact_value,
    }
  }
}

export const CreatePowerRequest = z.object({
  name: z.string(),
  price: z.number(),
  impactValue: z.number(),
  salesStartAt: z.number().optional(),
  salesEndAt: z.number().optional(),
  status: PowerStatus,
})

export type CreatePowerRequest = z.infer<typeof CreatePowerRequest>

export const UpdatePowerRequest = z.object({
  name: z.string().optional(),
  price: z.number().optional(),
  impactValue: z.number().optional(),
  salesStartAt: z.number().optional(),
  salesEndAt: z.number().optional(),
  status: PowerStatus.optional(),
  updateIcon: z.boolean().optional(),
  updateEffect: z.boolean().optional(),
})

export type UpdatePowerRequest = z.infer<typeof UpdatePowerRequest>
